import React, { useEffect, useState } from 'react';
import * as mobxReact from 'mobx-react';
import { injectIntl, IntlShape } from 'react-intl';
import Text from '../../../../../global/text/text';
import FilterPills from '../../../shared/filter-pills/filter-pills';
import ReportFilter from '../../../shared/report-filter/report-filter';
import { FilterField, IConfigurationFilter, ISelectableFilter, ReportType } from '../../../../../../api/reports.dao';
import { ReportConfigurationViewModel } from '../../report-configuration/report-configuration.viewmodel';
import ReportsViewModel from '../../reports.viewmodel';
import classNames from 'classnames';
import ListOfFilters from '../../../shared/list-of-filters/list-of-filters';
import DefaultButton from '../../../../../global/default-button/default-button';
import { MenuIcon } from '../../../../../global/icons/icons';
import { LayoutOption } from '../layout-options/layout-options';
import { loadingText } from '../../../../../../constants/common-labels.constants';

interface IProps {
    intl: IntlShape;
    configurationVm: ReportConfigurationViewModel;
    vm: ReportsViewModel;
}

const EventReportFiltersTab = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl, configurationVm, vm } = props;
    const { availableFilterTypes, withLayout, layoutType, applyLayout } = configurationVm;
    const [isSelectingFilter, setIsSelectingFilter] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<ISelectableFilter | null>(availableFilterTypes[0]);
    const [currentFilters, setCurrentFilters] = useState<ISelectableFilter[] | null>([]);
    const { clearFilter, clearAllFilters, getAppliedFilters, layoutSpecificFields, isLoadingLayoutFields, type } = configurationVm;

    useEffect(() => {
        setCurrentFilters([...availableFilterTypes]);
    }, []);

    const isContentHidden = !isSelectingFilter && !selectedFilter;

    const handleSelectFilter = (filter: ISelectableFilter) => {
        setSelectedFilter(filter);
        setIsSelectingFilter(false);
    };

    const openFilterSelection = () => {
        setSelectedFilter(null);
        setIsSelectingFilter(true);
    };

    let appliedFiltersWithDateFilter: IConfigurationFilter[] = [
        vm.hasSelectedDateRange() && {
            field: FilterField.MEETING_DATE,
            values: [],
        },
        ...getAppliedFilters()
    ].filter(Boolean);

    useEffect(() => {
        if (selectedFilter && !selectedFilter.isRequired && !layoutSpecificFields.find(el => el.fieldId === selectedFilter.field)) {
            setSelectedFilter(null);
        }
    }, [layoutSpecificFields]);

    return (
        <div className={classNames('generate-event-report__wrapper', {
            'generate-event-report__wrapper--content-hidden': isContentHidden
        })}>
            <div className={classNames('generate-event-report__sidebar', {
                'generate-event-report__sidebar--with-layout': withLayout
            })}>
                <div className='generate-event-report__sidebar-header'>
                    <DefaultButton
                        className={classNames('generate-event-report__list-button', {
                            'generate-event-report__list-button--selected': isSelectingFilter
                        })}
                        text={intl.formatMessage({
                            id: 'oe.reports.listOfFilters',
                            description: 'Header label',
                            defaultMessage: 'List of Filters'
                        })}
                        icon={MenuIcon}
                        onClick={openFilterSelection}
                        modifier='link'
                    />
                </div>
                {withLayout && (
                    <LayoutOption
                        layoutType={layoutType}
                        applyLayout={applyLayout}
                    />
                )}
                <div className='generate-event-report__applied-filters'>
                    <Text
                        className='generate-event-report__label'
                        message={intl.formatMessage({
                            id: 'oe.reports.appliedFilters',
                            defaultMessage: 'Applied Filters',
                            description: 'Label Text'
                        })}
                    />
                    {!appliedFiltersWithDateFilter.length && <Text
                        className='generate-event-report__no-applied-filters'
                        message={intl.formatMessage({
                            id: 'oe.reports.noFiltersApplied',
                            defaultMessage: 'No filters are applied.',
                            description: 'Label Text'
                        })}
                    />}
                    <FilterPills
                        vm={vm}
                        clearAll={clearAllFilters}
                        clear={filter => clearFilter(filter.field)}
                        filters={appliedFiltersWithDateFilter}
                        select={handleSelectFilter}
                        selected={selectedFilter}
                        availableFilterTypes={availableFilterTypes}
                    />
                </div>
            </div>
            {!isContentHidden && <div className='generate-event-report__content'>
                {type === ReportType.ATTENDEE_INTERACTIONS_REPORT && isLoadingLayoutFields
                    ? loadingText()
                    : <>
                        {isSelectingFilter && <ListOfFilters
                            onSelect={filter => handleSelectFilter(filter)}
                            filters={currentFilters}
                            configurationVm={configurationVm}
                        />}
                        {!isSelectingFilter && selectedFilter && <ReportFilter
                            vm={vm}
                            configurationVm={configurationVm}
                            {...selectedFilter}
                        />}
                    </>
                }
            </div>}
        </div>
    );
}));

export default EventReportFiltersTab;
